/* eslint-disable no-console */
// CORE
// Este é um arquivo core do boilerplate e modificações neste arquivo serão bloqueadas via pré-hook de commit.
// Fazemos este bloqueio para manter a consistência do boilerplate e diminuir as chances de conflitos nos updates.
// Caso possível, procure uma alternativa ao uso deste arquivo.
// Se a alteração for realmente necessária ou for um update do boilerplate,
// realize o commit usando o comando 'git commit --no-verify'.

import { handleSubmitError } from '@/components/notification/defaults';
import { Formik, Form as FormikForm, getIn, setIn } from 'formik';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import * as yup from 'yup';

import { isFormGroupField, splitFormGroupFieldName } from './formUtils';
import { FormContext, type FormContextValue } from './FormContext';
// These is here to avoid breaking code that expected
// Form to export FormContext and FormGroup
export { FormContext };
export * from './FormGroup';

const FORM_DATA_PREFIX = 'form_data_';

type SerializeMap = Record<string, (value: unknown) => unknown>;
type DeserializeMap = Record<string, (value: unknown) => unknown>;
type CallbackMap = Record<
  string,
  Record<string, (a: unknown, b: unknown, c: any, d?: any) => unknown>
>;

export interface FormProps {
  id?: string;
  disableLocalStorage?: boolean;
  handleSubmit: (
    values: Record<string, any>,
    submitProps?: unknown,
  ) => Promise<void | unknown>;
  disableSuccessToast?: boolean;
  initialStep?: number;
  children?: React.ReactNode;
}

type FormState = {
  customValidation: Record<string, unknown>;
  initialValues: Record<string, unknown>;
  initialValuesLocalStorage: Record<string, unknown> | null;
  validationSchema: Record<string, unknown>;
  serializeFuncList: SerializeMap;
  deserializeFuncList: DeserializeMap;
  formLoaded: boolean;
  callbackFuncList: CallbackMap;
  lastValues: any;
  currentStep: number;
  fieldStepMap: Record<string, number>;
  stepConfig: { index: number; label: string; Icon?: React.ReactNode }[];
};

export class Form extends React.Component<FormProps, FormState> {
  formId: string;
  constructor(props: FormProps) {
    super(props);
    this.formId =
      props.id || `form_${Math.random().toString(36).substring(2, 11)}`;

    const {
      initialValues: initialValuesLocalStorage,
      currentStep: currentStepLocalStorage,
    } = this.loadFromLocalStorage(props);

    if (
      this.props?.initialStep != null &&
      !Number.isInteger(this.props?.initialStep)
    ) {
      throw new Error('Initial step must be an integer');
    }

    this.state = {
      customValidation: {},
      initialValues: {},
      initialValuesLocalStorage: initialValuesLocalStorage ?? null,
      validationSchema: {},
      serializeFuncList: {},
      deserializeFuncList: {},
      formLoaded: false,
      callbackFuncList: {},
      lastValues: null,
      currentStep: currentStepLocalStorage ?? this.props?.initialStep ?? 0,
      fieldStepMap: {},
      stepConfig: [],
    };
  }

  loadFromLocalStorage = (props: FormProps) => {
    let initialValues: Record<string, unknown> | null = null;
    let currentStep: number | undefined;

    if (!props.disableLocalStorage && typeof window !== 'undefined') {
      const savedData = localStorage.getItem(
        `${FORM_DATA_PREFIX}${this.formId}`,
      );
      const savedStep = localStorage.getItem(
        `${FORM_DATA_PREFIX}${this.formId}_currentStep`,
      );
      if (savedData) {
        try {
          initialValues = JSON.parse(savedData);
          currentStep = JSON.parse(savedStep as string);
        } catch (error) {
          console.error('Error loading form data from localStorage:', error);
        }
      }
    }

    return { initialValues, currentStep };
  };

  saveToLocalStorage = (values: Record<string, unknown>) => {
    if (this.props.disableLocalStorage) return;
    if (!values || Object.keys(values).length === 0) return;
    if (
      typeof window !== 'undefined' &&
      window.location.pathname.startsWith('/auth')
    )
      return;
    const cleanValues: Record<string, unknown> = { ...values };

    const sensitiveFields = ['key', 'password', 'senha', 'chave', 'token'];
    Object.keys(values).forEach((key) => {
      if (sensitiveFields.some((field) => key.toLowerCase().includes(field))) {
        delete (cleanValues as any)[key];
      }
    });

    try {
      localStorage.setItem(
        `${FORM_DATA_PREFIX}${this.formId}`,
        JSON.stringify(this.serializeValues(cleanValues)),
      );
      localStorage.setItem(
        `${FORM_DATA_PREFIX}${this.formId}_currentStep`,
        JSON.stringify(this.state.currentStep),
      );
    } catch (error) {
      console.error('Error saving form data to localStorage:', error);
    }
  };

  clearLocalStorage = () => {
    if (this.props.disableLocalStorage) return;

    try {
      localStorage.removeItem(`${FORM_DATA_PREFIX}${this.formId}`);
    } catch (error) {
      console.error('Error clearing form data from localStorage:', error);
    }
  };

  getFormContext = (formikProps: any): FormContextValue => {
    return {
      values: formikProps.values,
      errors: formikProps.errors,
      touched: formikProps.touched,
      initialValues: formikProps.initialValues,
      validate: formikProps.validateForm,
      isSubmitting: formikProps.isSubmitting,
      isValid: formikProps.isValid,
      submitForm: formikProps.submitForm,
      setFieldValue: formikProps.setFieldValue,
      setFieldTouched: (
        name: string,
        isTouched?: boolean,
        shouldValidate?: boolean,
      ) => {
        formikProps.setFieldTouched(name, isTouched, shouldValidate);
        this.setCustomErrorState(name, null, () => {
          formikProps.validateForm();
        });
      },
      setCustomError: (name: string, error: string | null) => {
        this.setCustomErrorState(name, error, () => {
          formikProps.validateForm();
        });
      },
      setInitialValue: this.setInitialValue,
      setCallbackFunc: this.setCallbackFunc,
      setSerializeFunc: this.setSerializeFunc,
      setDeserializeFunc: this.setDeserializeFunc,
      currentStep: this.state.currentStep,
      fieldStepMap: this.state.fieldStepMap,
      stepConfig: this.state.stepConfig,
      setCurrentStep: this.setCurrentStep,
      setFieldStepMap: this.setFieldStepMap,
      setStepConfig: this.setStepConfig,
      setValidationSchema: this.setValidationSchema,
    };
  };

  setCustomErrorState = (name: string, error: any, callback?: () => void) => {
    const isFormGroup = isFormGroupField(name);
    let fieldIndex: any;
    let fieldName: any;
    if (isFormGroup) {
      const splittedName = splitFormGroupFieldName(name);
      name = splittedName.groupName;
      fieldIndex = splittedName.fieldIndex;
      fieldName = splittedName.fieldName;
    }
    this.setState((state) => {
      if (error == null && (state.customValidation as any)[name] != null) {
        if (!isFormGroup) {
          delete (state.customValidation as any)[name];
        } else {
          delete (state.customValidation as any)[name][fieldIndex][fieldName];
          if (
            (state.customValidation as any)[name].every((group: any) => {
              return group == null || Object.entries(group).length === 0;
            })
          ) {
            delete (state.customValidation as any)[name];
          }
        }
      } else if (error != null) {
        (state.customValidation as any)[name] = error;
      }
      return { ...state, formLoaded: true };
    }, callback);
  };

  setInitialValue = (name: string, value: unknown) => {
    this.setState((state) => {
      const newInitialValues = { ...state.initialValues };
      if (value == null) {
        delete (newInitialValues as any)[name];
      } else {
        (newInitialValues as any)[name] = value as any;
      }
      return { ...state, initialValues: newInitialValues, formLoaded: true };
    });
  };

  setValidationSchema = (name: string, schema: unknown) => {
    this.setState((state) => {
      const newValidationSchema = { ...state.validationSchema };
      if (schema == null) {
        delete (newValidationSchema as any)[name];
      } else {
        (newValidationSchema as any)[name] = schema as any;
      }
      return {
        ...state,
        validationSchema: newValidationSchema,
        formLoaded: true,
      };
    });
  };

  setSerializeFunc = (
    name: string,
    serializeFunc?: (v: unknown) => unknown,
  ) => {
    this.setState((state) => {
      const newSerializeFuncList = { ...state.serializeFuncList };
      if (serializeFunc == null) {
        delete newSerializeFuncList[name];
      } else {
        newSerializeFuncList[name] = serializeFunc as any;
      }
      return {
        ...state,
        serializeFuncList: newSerializeFuncList,
        formLoaded: true,
      };
    });
  };

  setDeserializeFunc = (
    name: string,
    deserializeFunc?: (v: unknown) => unknown,
  ) => {
    this.setState((state) => {
      const newDeserializeFuncList = { ...state.deserializeFuncList };
      if (deserializeFunc == null) {
        delete newDeserializeFuncList[name];
      } else {
        newDeserializeFuncList[name] = deserializeFunc as any;
      }
      return {
        ...state,
        deserializeFuncList: newDeserializeFuncList,
        formLoaded: true,
      };
    });
  };

  setCurrentStep = (nextStep: number | ((prev: number) => number)) => {
    this.setState((state) => {
      const newCurrentStep =
        typeof nextStep === 'function'
          ? (nextStep as (prev: number) => number)(state.currentStep)
          : nextStep;
      return { ...state, currentStep: newCurrentStep };
    });
  };

  setFieldStepMap = (fieldName: string, stepIndex: number) => {
    this.setState((state) => {
      return {
        ...state,
        fieldStepMap: { ...state.fieldStepMap, [fieldName]: stepIndex },
      };
    });
  };

  setStepConfig = (
    stepIndex: number,
    stepLabel: string,
    StepIcon?: React.ReactNode,
  ) => {
    if (!Number.isInteger(stepIndex)) {
      throw new Error('Step index must be an integer.');
    }
    this.setState((state) => {
      const configWithRemovedIndex = state.stepConfig.filter((element) => {
        return element.index !== stepIndex;
      });
      return {
        ...state,
        stepConfig: [
          ...configWithRemovedIndex,
          { index: stepIndex, label: stepLabel, Icon: StepIcon },
        ],
      };
    });
  };

  serializeValues = (values: Record<string, any>, removeUnset = false) => {
    const orderedTranformFunction = Object.entries(
      this.state.serializeFuncList,
    );
    orderedTranformFunction.sort(
      (a, b) => b[0].split('.').length - a[0].split('.').length,
    );
    let valuesTransformed = values;
    orderedTranformFunction.forEach(([name, func]) => {
      const currentValue = getIn(valuesTransformed, name);
      if (func && currentValue != undefined) {
        const returnValue = func(currentValue);
        if (returnValue == undefined && removeUnset) {
          delete (valuesTransformed as any)[name];
        } else {
          valuesTransformed = setIn(valuesTransformed, name, returnValue);
        }
      }
    });
    return valuesTransformed;
  };

  serializeValuesForSubmit = (values: Record<string, any>) => {
    return this.serializeValues(values, true);
  };

  deserializeValues = (values: Record<string, any>) => {
    if (!this.state.formLoaded) return null;

    const orderedTranformFunction = Object.entries(
      this.state.deserializeFuncList,
    );
    orderedTranformFunction.sort(
      (a, b) => a[0].split('.').length - b[0].split('.').length,
    );
    let valuesTransformed = values;
    orderedTranformFunction.forEach(([name, func]) => {
      const currentValue = getIn(valuesTransformed, name);
      if (func && currentValue != undefined) {
        const returnValue = func(currentValue);
        if (returnValue == undefined) {
          delete (valuesTransformed as any)[name];
        } else {
          valuesTransformed = setIn(valuesTransformed, name, returnValue);
        }
      }
    });
    return valuesTransformed;
  };

  setCallbackFunc = (
    listenerField: string,
    dependentField: string,
    callbackFunc: (a: unknown, b: unknown, c: any, d?: any) => unknown,
  ) => {
    this.setState((state) => {
      if (!state.callbackFuncList[dependentField])
        state.callbackFuncList[dependentField] = {} as any;
      state.callbackFuncList[dependentField][listenerField] =
        callbackFunc as any;
      return state;
    });
  };

  triggerCallbackFunc = (
    values: any,
    setValue: (name: string, v: unknown) => void,
  ) => {
    Object.entries(this.state.callbackFuncList).forEach(
      ([dependentField, callbackFuncList]) => {
        if (
          getIn(this.state.lastValues, dependentField) !=
          getIn(values, dependentField)
        ) {
          Object.entries(callbackFuncList).forEach(
            ([listenerField, callbackFunc]) => {
              let groupValues = undefined as any;
              if (listenerField.includes('.')) {
                const groupPrefix = listenerField
                  .split('.')
                  .slice(0, -1)
                  .join('.');
                groupValues = getIn(values, groupPrefix);
              }
              const returnValue = (callbackFunc as any)(
                getIn(values, dependentField),
                getIn(values, listenerField),
                values,
                groupValues,
              );
              if (returnValue != undefined) {
                setValue(listenerField, returnValue);
              }
            },
          );
        }
      },
    );
  };

  render() {
    const submit = (values: Record<string, any>, formikBag: any) => {
      const submitProps = values['__UNSET__submit_props'];
      delete values['__UNSET__submit_props'];

      return this.props
        .handleSubmit(this.serializeValuesForSubmit(values), submitProps)
        .then(() => {
          if (!this.props.disableLocalStorage) {
            this.clearLocalStorage();
          }

          if (!this.props.disableSuccessToast)
            enqueueSnackbar('Sucesso!', { variant: 'success' });
        })
        .catch((error) => {
          handleSubmitError(
            error,
            (name: string, errorMsg: string | string[]) => {
              const errorMessage = Array.isArray(errorMsg) ? errorMsg.join(', ') : errorMsg;
              this.setCustomErrorState(name, errorMessage, () => {
                formikBag.validateForm();
              });
            },
            this.setCurrentStep,
            this.state.fieldStepMap,
          );
        })
        .finally(() => {
          formikBag.setFieldValue('__UNSET__submit_props', undefined);
        });
    };

    return (
      <Formik
        onSubmit={submit}
        initialValues={
          this.deserializeValues(this.state.initialValuesLocalStorage ?? {}) ??
          this.state.initialValues
        }
        validationSchema={yup
          .object()
          .shape(this.state.validationSchema as any)}
        validateOnMount
        validate={() => this.state.customValidation}
        enableReinitialize
      >
        {(formikProps) => {
          React.useEffect(() => {
            if (!this.props.disableLocalStorage) {
              this.saveToLocalStorage(formikProps.values);
            }
          }, [
            JSON.stringify(formikProps.values),
            this.props.disableLocalStorage,
            this.state.currentStep,
          ]);

          React.useEffect(() => {
            this.triggerCallbackFunc(
              formikProps.values,
              formikProps.setFieldValue,
            );
            this.setState({ lastValues: formikProps.values });
          }, [JSON.stringify(formikProps.values)]);

          return (
            <FormikForm>
              <FormContext.Provider value={this.getFormContext(formikProps)}>
                {this.props.children}
              </FormContext.Provider>
            </FormikForm>
          );
        }}
      </Formik>
    );
  }
}

export function cleanLocalStorageValues() {
  if (typeof window !== 'undefined') {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith(FORM_DATA_PREFIX)) {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.error(`Error removing ${key} from localStorage:`, error);
        }
      }
    });
  }
}
